/**
 * English About Translations
 * 关于页翻译模块 - 英语
 * 
 * 包含公司介绍、发展历程、使命价值观、认证专利等翻译
 * 
 * SEO 关键词覆盖：
 * - infrared heater manufacturer / supplier China
 * - wholesale infrared heater / OEM ODM
 * - infrared heater for restaurant / patio / warehouse / office
 * - far infrared radiant heater
 */

import type { AboutTranslations } from '../../types';

export const enAbout: AboutTranslations = {
  title: 'About Us',
  subtitle: 'Your trusted infrared heater manufacturer and supplier from China.',
  header: {
    title: 'About JH Heatsup',
    subtitle: 'Your trusted infrared heater manufacturer and supplier from China.',
  },
  history: {
    title: 'Our History',
    desc: 'Founded in 2006, Fujian Jinghui Environmental Technology Co., Ltd. (JH Heatsup) is a leading infrared heater manufacturer in China. Starting with a simple vision — to create heating solutions that are powerful, energy-efficient, and environmentally responsible — we have grown from a small R&D team into a global manufacturer exporting to 80+ countries.',
    timelineTitle: 'Infrared Heating Milestones',
    timelineDesc: 'Six milestones that trace our progress from the first far-infrared heater series to connected, fast-heating bathroom solutions.',
    milestones: [
      { year: '2013', title: 'Far-Infrared Production Begins', desc: 'The 13A and 13B series far-infrared radiant heaters entered production.' },
      { year: '2015', title: 'The Product Family Expands', desc: 'We launched the 14A/14B and 15A/15B far-infrared radiant heater series.' },
      { year: '2017', title: 'Heating Meets Bluetooth', desc: 'The JH13C series launched with an integrated Bluetooth speaker.' },
      { year: '2018', title: 'Solutions for More Spaces', desc: 'Baseboard heaters reached the market, followed by bathroom far-infrared heater production.' },
      { year: '2019', title: 'Smart Heating with Wi-Fi', desc: 'The connected 13W heater series launched with built-in Wi-Fi functionality.' },
      { year: '2021', title: 'Five-Second Bathroom Heat', desc: 'We developed a glass-panel bathroom heater capable of heating in just five seconds.' },
    ],
  },
  mission: {
    title: 'Our Mission',
  },
  quality: {
    title: 'Quality Assurance',
    desc: 'ISO9001 certified manufacturing processes. Every infrared heater is rigorously tested before it leaves our factory, ensuring consistent quality for wholesale and OEM orders.',
  },
  values: {
    title: 'Our Core Values',
    sustainability: {
      title: 'Sustainability & Innovation',
      desc: 'Our far-infrared heaters use up to 80% less energy than traditional convection heaters. We continuously innovate energy-saving technologies to lower carbon footprints while maximizing heating efficiency.',
    },
    quality: {
      title: 'Certified Quality',
      desc: 'Built in ISO9001-certified facilities and tested to international standards including CE, CB, SAA, and RoHS. Every product meets the strictest safety and performance requirements.',
    },
    customerFirst: {
      title: 'Global Trust & Partnership',
      desc: 'Trusted by 800+ global partners across 80+ countries as their infrared heater supplier. We build long-term relationships through reliable OEM/ODM service, on-time delivery, and customer-first support.',
    },
  },
  features: {
    title: 'Why Choose Our Infrared Heaters',
    fast: {
      title: 'Fast Heating',
      desc: 'Infrared heaters reach full operating temperature in just 5 minutes. Instant radiant warmth for patios, restaurants, and outdoor spaces — no waiting.',
    },
    energy: {
      title: 'Energy Saving',
      desc: 'Up to 99% energy conversion efficiency. Our smart WiFi control lets you reduce heating costs by scheduling and adjusting temperature remotely.',
    },
    healthy: {
      title: 'Healthy Comfort',
      desc: 'Far-infrared radiant heat warms people and objects directly without drying the air or circulating dust. Safe for indoor use, allergy-friendly, and completely silent.',
    },
    safe: {
      title: 'Safe & Reliable',
      desc: 'IPX4 waterproof rating, overheat protection, and tip-over switches on portable models. Our heaters are safe to run all night and built for 25+ years of operation.',
    },
  },
  companyProfile: {
    title: 'Company Profile',
    desc: 'Fujian Jinghui Environmental Technology Co., Ltd. (JH Heatsup) is a professional infrared heater manufacturer and supplier based in Fuzhou, China. Established in 2006, we specialize in the R&D, production, and export of far-infrared radiant heaters. Our product range includes wall-mounted infrared panel heaters, portable carbon fiber heaters, halogen patio heaters, and ceiling-mounted heaters — all designed for energy-efficient, silent, and healthy heating. We offer OEM and ODM services, supporting custom branding, packaging, and product development for wholesale buyers worldwide.',
  },
  services: {
    title: 'Professional Services',
    desc: 'As an experienced infrared heater supplier, we provide end-to-end support for B2B partners: custom product design, OEM/ODM manufacturing, quality inspection, logistics coordination, and after-sales technical support. Our heaters serve diverse applications including restaurant patio heating, warehouse and workshop warming, office and living room comfort, gym and sauna heating, and outdoor entertainment areas.',
  },
  technology: {
    title: 'Leading Far-Infrared Technology',
    desc: 'Our proprietary nano-ceramic coating technology sets us apart from other infrared heater manufacturers. Applied to our far-infrared heating elements, this patented coating eliminates the paint discoloration and peeling issues common in traditional heaters, while boosting radiant efficiency to 86% emissivity. The result is faster heating, lower energy consumption, and a longer product lifespan.',
    nanoCeramic: { title: 'Nano-Ceramic Technology', desc: 'Patented coating with 86% emissivity for superior radiant efficiency' },
    farInfrared: { title: 'Far-Infrared Radiation', desc: '8-15 μm wavelength optimized for human body absorption and comfort' },
  },
  certifications: {
    title: 'Certifications & Patents',
    desc: 'We hold 32+ patents for appearance, utility models, and inventions. Our products are certified to international standards: ISO9001:2015, CCC, CE (Europe), CB (International), SAA (Australia), EMC, and SASO (Saudi Arabia). These certifications ensure our heaters meet safety and quality requirements for import into any market worldwide.',
  },
  clients: {
    title: 'Global Clients',
    desc: 'As a leading infrared heater manufacturer in China, our products are exported to 80+ countries and regions worldwide, with strong presence in Europe, Australia, the Middle East, and Southeast Asia.',
    export: 'Export to 80+ countries and regions',
    agents: '800+ international distribution partners',
    trusted: 'Trusted infrared heater supplier since 2006',
    showcaseTitle: 'Trusted by Partners Worldwide',
    showcaseDesc: 'At international trade fairs, we meet face to face with distributors, project buyers, and industry professionals from around the world. These conversations help us understand local heating needs, share our latest infrared solutions, and build lasting partnerships through reliable products, consistent quality, and responsive support.',
  },
  manufacturing: {
    title: 'Manufacturing Capability',
    desc: 'Our 4,500 m² production facility houses 150+ technical engineers and skilled workers, producing 10,000+ infrared heaters per month. Whether you need a small trial order or a full container load, we have the capacity to deliver on time with consistent quality. Our flexible production lines support both standard models and custom OEM/ODM projects.',
    workshopArea: 'Workshop Area',
    engineersWorkers: 'Engineers & Workers',
    monthlyCapacity: 'Monthly Capacity',
    patents: 'Patents',
  },
  ceo: {
    title: 'Our Founder',
    name: 'Ms. Huang Xuefang',
    role: 'Founder',
    desc: 'Ms. Huang Xuefang is one of the earliest researchers and designers of far-infrared heaters in China. In 2003, she worked with a group of partners passionate about heating technology to develop evaporative products. In 2006, she founded Fujian Jinghui Environmental Technology Co., Ltd. In 2011, she led the R&D team to design board-type far-infrared radiant heaters, pioneering the integration of smart WiFi controls, Bluetooth speakers, and app connectivity into heaters — transforming them from simple appliances into connected lifestyle products.',
    quote: '"We are committed to creating a greener, warmer world through technology."',
  },
  applications: {
    title: 'Where Our Infrared Heaters Are Used',
    desc: 'Our far-infrared heaters are trusted across 80+ countries for diverse applications — from restaurant patios to warehouse workshops. Here are the most popular use cases.',
    restaurant: {
      title: 'Restaurant & Café',
      desc: 'Extend your outdoor dining season with energy-efficient patio heaters',
      icon: 'Utensils',
    },
    livingRoom: {
      title: 'Living Room',
      desc: 'Silent, dust-free radiant heating for comfortable home living',
      icon: 'Sofa',
    },
    office: {
      title: 'Office',
      desc: 'Zone heating that keeps workspaces warm without central heating waste',
      icon: 'Building',
    },
    patio: {
      title: 'Patio & Outdoor',
      desc: 'Waterproof infrared heaters for year-round outdoor comfort',
      icon: 'Sun',
    },
    sauna: {
      title: 'Sauna & Wellness',
      desc: 'Deep-penetrating far-infrared heat for spa and wellness applications',
      icon: 'Flame',
    },
    garage: {
      title: 'Garage & Workshop',
      desc: 'Powerful heating for garages, workshops, and warehouses',
      icon: 'Warehouse',
    },
    balcony: {
      title: 'Gym & Fitness',
      desc: 'Infrared warmth for yoga studios, gyms, and fitness centers',
      icon: 'Dumbbell',
    },
    entertainment: {
      title: 'Entertainment Room',
      desc: 'Cozy ambient heating with optional Bluetooth speaker integration',
      icon: 'Gamepad2',
    },
  },
};
